// VpnDailyReportPanel.jsx — tab "VPN Tunnel (Daily)" on the Periodic Report page.
// Backend: backend/vpnDailyReport.js (routes /api/periodic-report/vpn-daily/*),
// cron job key 'vpn_daily_report'. Data: net_tunnel_health (cron gre_pil_nat).
import { useState, useEffect, useCallback } from 'react';
import { api as axiosApi } from '../context/AuthContext';
import { Alert, Btn, Empty, useApi, fmtBytes, CronScheduler } from './tmShared';

const B = '/api/periodic-report/vpn-daily';

export default function VpnDailyReportPanel() {
  const api = useApi();
  const [cfg, setCfg] = useState(null);
  const [form, setForm] = useState(null);
  const [alert, setAlert] = useState(null);
  const [busy, setBusy] = useState('');
  const [prev, setPrev] = useState(null);
  const [date, setDate] = useState('');

  const load = useCallback(async () => {
    try {
      const d = await api(`${B}/config`);
      setCfg(d);
      setForm({
        enabled: !!Number(d.enabled), email_to: d.email_to || '', email_cc: d.email_cc || '', email_bcc: d.email_bcc || '',
        sender_name: d.sender_name || '', report_title: d.report_title || '',
        window_end_hour: Number(d.window_end_hour ?? 7), trend_days: Number(d.trend_days || 7), min_event_minutes: Number(d.min_event_minutes ?? 15), email_focus_local: d.email_focus_local ?? '10.40.0.0/23',
        send_when_clean: !!Number(d.send_when_clean), resend_every_run: !!Number(d.resend_every_run),
      });
    } catch (e) { setAlert({ type: 'error', msg: e.message }); }
  }, [api]);
  useEffect(() => { load(); }, [load]);

  const save = async () => {
    setBusy('save'); setAlert(null);
    try {
      setCfg(await api(`${B}/config`, { method: 'PUT', body: JSON.stringify(form) }));
      setAlert({ type: 'success', msg: 'Configuration saved.' });
    } catch (e) { setAlert({ type: 'error', msg: e.message }); }
    finally { setBusy(''); }
  };
  const body = () => JSON.stringify({ date: date || undefined });
  const doPreview = async () => {
    setBusy('preview'); setAlert(null); setPrev(null);
    try {
      const d = await api(`${B}/preview`, { method: 'POST', body: body() });
      if (d.skipped) setAlert({ type: 'error', msg: `Skipped: ${d.reason}` }); else setPrev(d);
    } catch (e) { setAlert({ type: 'error', msg: e.message }); }
    finally { setBusy(''); }
  };
  const doSend = async () => {
    if (!window.confirm(`Send the VPN tunnel daily report now to ${cfg?.email_to || '(empty)'}?`)) return;
    setBusy('send'); setAlert(null);
    try {
      const d = await api(`${B}/send`, { method: 'POST', body: body() });
      setAlert(d.sent ? { type: 'success', msg: `Sent ${d.period} to ${d.to} (${fmtBytes(d.bytes)}).` }
        : { type: 'error', msg: `Not sent: ${d.reason || d.error || '-'}` });
      load();
    } catch (e) { setAlert({ type: 'error', msg: e.message }); }
    finally { setBusy(''); }
  };
  const doXlsx = async () => {
    setBusy('xlsx'); setAlert(null);
    try {
      const res = await axiosApi.get(`${B}/xlsx${date ? `?date=${date}` : ''}`, { responseType: 'blob' });
      const m = (res.headers['content-disposition'] || '').match(/filename="?([^"]+)"?/);
      const name = (m && m[1]) || 'vpn-tunnel-daily.xlsx';
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a'); a.href = url; a.download = name;
      document.body.appendChild(a); a.click(); a.remove(); window.URL.revokeObjectURL(url);
    } catch (e) { setAlert({ type: 'error', msg: e.message }); }
    finally { setBusy(''); }
  };

  const card = { background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 10, padding: '14px 16px', marginBottom: 16 };
  const label = { fontSize: 11, color: '#64748b', fontWeight: 600, marginBottom: 4, textTransform: 'uppercase', letterSpacing: '.03em' };
  const input = { padding: '7px 10px', fontSize: 13, border: '1px solid #cbd5e1', borderRadius: 6, fontFamily: 'inherit' };
  const chk = { display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, color: '#334155' };
  const hint = { fontSize: 11, color: '#94a3b8', marginTop: 3, lineHeight: 1.5 };
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.type === 'checkbox' ? e.target.checked : e.target.value }));

  if (!form) return <><Alert msg={alert?.msg} type={alert?.type} onClose={() => setAlert(null)} /><Empty icon="⏳" text="Loading…" /></>;

  return (
    <div>
      <Alert msg={alert?.msg} type={alert?.type} onClose={() => setAlert(null)} />
      <div style={{ fontSize: 12.5, color: '#475569', marginBottom: 12, lineHeight: 1.6 }}>
        Daily email covering <b>every tunnel</b>, tunnel by tunnel: AWS Site-to-Site VPN in the <b>PIL and Indomas</b> accounts (per outside IP),
        <b>WatchGuard Jakarta BOVPN</b> SAs to Dumai, Kijing and Medan (per local → remote route) and the GRE PGOC egress.
        Lists every <b>degrade/down event</b> (start, recovery, duration) plus availability for the last 24 hours and per day.
        Only tunnels that were <b>up</b> in the window are monitored; configured tunnels that never came up are ignored.
        Data: <code>net_tunnel_health</code> + <code>net_vpn_tunnels</code> (cron <code>gre_pil_nat</code>), <code>firebox_vpn_snapshot</code> (cron <code>firebox_vpn</code>) — both jobs must be enabled.
      </div>

      <CronScheduler key="vpn_daily_report" api={api} jobKey="vpn_daily_report"
        runConfirm="Run the VPN tunnel daily report now (cron trigger)?" />

      <div style={{ ...card, marginTop: 14 }}>
        <div style={{ fontSize: 13, fontWeight: 700, color: '#334155', marginBottom: 12 }}>⚙️ Configuration</div>
        <label style={{ ...chk, fontWeight: 600, marginBottom: 6 }}>
          <input type="checkbox" checked={form.enabled} onChange={set('enabled')} /> Enable automatic delivery (when cron fires)
        </label>
        <label style={{ ...chk, marginBottom: 6 }}>
          <input type="checkbox" checked={form.send_when_clean} onChange={set('send_when_clean')} /> Send even when there were no events
        </label>
        <label style={{ ...chk, marginBottom: 12 }}>
          <input type="checkbox" checked={form.resend_every_run} onChange={set('resend_every_run')} /> Resend on every scheduled run (skip the once-per-day check)
        </label>

        <div style={{ marginBottom: 12 }}>
          <div style={label}>Recipients (To)</div>
          <input type="text" value={form.email_to} onChange={set('email_to')} placeholder="it@pacificindomas.com, ..."
            style={{ ...input, width: 480, maxWidth: '100%' }} />
        </div>
        <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', marginBottom: 12 }}>
          <div><div style={label}>CC (optional)</div>
            <input type="text" value={form.email_cc} onChange={set('email_cc')} style={{ ...input, width: 320, maxWidth: '100%' }} /></div>
          <div><div style={label}>BCC (optional)</div>
            <input type="text" value={form.email_bcc} onChange={set('email_bcc')} style={{ ...input, width: 320, maxWidth: '100%' }} /></div>
        </div>
        <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', marginBottom: 12 }}>
          <div><div style={label}>Report title (email subject)</div>
            <input type="text" value={form.report_title} onChange={set('report_title')} style={{ ...input, width: 320 }} /></div>
          <div><div style={label}>Sender name</div>
            <input type="text" value={form.sender_name} onChange={set('sender_name')} style={{ ...input, width: 240 }} /></div>
        </div>
        <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', marginBottom: 4 }}>
          <div><div style={label}>24h window ends at (WIB)</div>
            <select value={form.window_end_hour} onChange={(e) => setForm((f) => ({ ...f, window_end_hour: Number(e.target.value) }))}
              style={{ ...input, width: 120 }}>
              {Array.from({ length: 24 }, (_, h) => <option key={h} value={h}>{String(h).padStart(2, '0')}:00</option>)}
            </select></div>
          <div><div style={label}>Trend (days)</div>
            <input type="number" min={1} max={31} value={form.trend_days}
              onChange={(e) => setForm((f) => ({ ...f, trend_days: e.target.value }))} style={{ ...input, width: 90 }} /></div>
          <div><div style={label}>Ignore flaps shorter than (min)</div>
            <input type="number" min={0} max={240} value={form.min_event_minutes}
              onChange={(e) => setForm((f) => ({ ...f, min_event_minutes: e.target.value }))} style={{ ...input, width: 90 }} /></div>
          <div><div style={label}>Email body: BOVPN routes from</div>
            <input type="text" value={form.email_focus_local} placeholder="empty = all tunnels"
              onChange={(e) => setForm((f) => ({ ...f, email_focus_local: e.target.value }))} style={{ ...input, width: 150 }} /></div>
        </div>
        <div style={hint}>Schedule the cron a few minutes after the window end (e.g. window 07:00 → cron 07:05) so the last sample is in.
          Flaps that recover within the minimum (e.g. idle BOVPN SAs renegotiating) are not counted as events or downtime; 0 = count everything.
          The email body shows only WatchGuard BOVPN routes whose local network is the value above (default 10.40.0.0/23, the AWS Jakarta VPC, to every site subnet/VLAN); the attached Excel always holds the full report. Leave it empty to put every tunnel in the body.
          SMTP comes from the ASE Alert tab.</div>

        <div style={{ marginTop: 12 }}>
          <Btn size="sm" variant="primary" disabled={!!busy} onClick={save}>{busy === 'save' ? '⏳ Saving…' : '💾 Save configuration'}</Btn>
        </div>
        {cfg && (cfg.last_sent_period || cfg.last_status) && (
          <div style={{ fontSize: 12, color: '#64748b', marginTop: 10 }}>
            Last sent: <b>{cfg.last_sent_period || '—'}</b>{cfg.last_status ? ` · ${cfg.last_status}` : ''}
          </div>
        )}
      </div>

      <div style={card}>
        <div style={{ fontSize: 13, fontWeight: 700, color: '#334155', marginBottom: 12 }}>🧪 Manual test</div>
        <div style={{ display: 'flex', gap: 12, alignItems: 'flex-end', flexWrap: 'wrap' }}>
          <div><div style={label}>Report date (window end, optional)</div>
            <input type="date" value={date} onChange={(e) => setDate(e.target.value)} style={{ ...input, width: 170 }} /></div>
          <Btn size="sm" disabled={!!busy} onClick={doPreview}>{busy === 'preview' ? '⏳ Building…' : '👁️ Preview (no delivery)'}</Btn>
          <Btn size="sm" disabled={!!busy} onClick={doXlsx}>{busy === 'xlsx' ? '⏳ Building…' : '⬇️ Download .xlsx'}</Btn>
          <Btn size="sm" variant="danger" disabled={!!busy} onClick={doSend}>{busy === 'send' ? '⏳ Sending…' : '📧 Send now'}</Btn>
        </div>
        <div style={hint}>Leave the date empty to use the latest completed 24-hour window.</div>
      </div>

      {prev && (
        <div style={card}>
          <div style={{ fontSize: 12, color: '#64748b', marginBottom: 10 }}>
            Period <b>{prev.period}</b> · Subject: <b>{prev.subject}</b> · {prev.status} · Attachment {fmtBytes(prev.bytes)}
          </div>
          <iframe title="preview" srcDoc={prev.html}
            style={{ width: '100%', height: 620, border: '1px solid #e2e8f0', borderRadius: 8, background: '#fff' }} />
        </div>
      )}
    </div>
  );
}
